import { Link, useNavigate } from '@tanstack/react-router';
import { Bell, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { formatDateTime } from '@/lib/format';
import { useOrgId } from '@/lib/org';
import {
  isClientRole,
  setBusinessViewPreview,
  useBusinessView,
  useMarkRead,
  useNotifications,
} from '@/lib/portal';
import { cn } from '@/lib/utils';

/** Top bar notifications (document 05): approvals waiting, uploads, VAT returns due. */
export function NotificationsBell() {
  const orgId = useOrgId();
  const n = useNotifications(Boolean(orgId));
  const read = useMarkRead();
  const navigate = useNavigate();
  const unread = n.data?.unread ?? 0;
  const list = n.data?.notifications ?? [];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}
          data-testid="notifications"
        >
          <Bell className="size-4" />
          {unread ? (
            <span className="absolute -right-0.5 -top-0.5 inline-flex min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground">
              {unread > 99 ? '99+' : unread}
            </span>
          ) : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[min(380px,calc(100vw-1.5rem))]">
        <div className="flex items-center justify-between">
          <DropdownMenuLabel>Notifications</DropdownMenuLabel>
          {unread ? (
            <Button
              variant="link"
              size="sm"
              className="h-auto px-2 text-xs"
              onClick={() => read.mutate(undefined)}
            >
              Mark all as read
            </Button>
          ) : null}
        </div>
        <DropdownMenuSeparator />
        {!list.length ? (
          <p className="px-2 py-3 text-sm text-muted-foreground">You have no notifications.</p>
        ) : (
          <div className="max-h-[420px] overflow-y-auto">
            {list.map((x) => (
              <DropdownMenuItem
                key={x.id}
                className="flex-col items-start gap-0.5"
                onSelect={() => {
                  if (!x.readAt) read.mutate([x.id]);
                  if (x.link) void navigate({ to: x.link });
                }}
              >
                <span className={cn('text-sm', !x.readAt && 'font-semibold')}>{x.title}</span>
                <span className="line-clamp-2 text-xs text-muted-foreground">{x.body}</span>
                <span className="text-[11px] text-muted-foreground">
                  {formatDateTime(x.createdAt)}
                </span>
              </DropdownMenuItem>
            ))}
          </div>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/settings/notifications">Notification settings</Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Outss staff: see this client's books as the client does (document 05, "Two views"). */
export function BusinessViewToggle() {
  const view = useBusinessView();
  if (isClientRole(view.role) || view.role === null) return null;
  return (
    <Button
      variant={view.preview ? 'default' : 'ghost'}
      size="sm"
      className="gap-1"
      aria-pressed={view.preview}
      title={view.preview ? 'Back to the Accountant view' : 'See what the client sees'}
      aria-label={view.preview ? 'Business view on' : 'Business view'}
      onClick={() => setBusinessViewPreview(!view.preview)}
    >
      <Eye className="size-4" />
      <span className="hidden sm:inline">
        {view.preview ? 'Business view on' : 'Business view'}
      </span>
    </Button>
  );
}
